import { Dialog, DialogContent, DialogHeader, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { DialogTitleWithBug } from "@/components/ui/dialog-title-with-bug";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";
import { Loader2, PlayCircle } from "lucide-react";
import { useProcessBulletin } from "@/contexts/ProcessBulletinContext";
import { useBrandManager } from "@/hooks/useBrandManager";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { createEdgeFunctionErrorHandler } from "@/utils/edgeFunction";
import { useMemo } from "react";
import { applyMidnightOffset } from "@/utils/dateFormatter";

export const ProcessBulletinDialog = () => {
  const { 
    isProcessBulletinOpen, 
    setIsProcessBulletinOpen,
    selectedCollectionId,
    setSelectedCollectionId,
    selectedProgramId,
    setSelectedProgramId
  } = useProcessBulletin();
  const { currentBrandId } = useBrandManager();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Fetch collections for processing
  const { data: collections } = useQuery({
    queryKey: ["newsroom-collections-for-bulletin", currentBrandId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("os_newsroom_collections")
        .select("*")
        .eq("brand_id", currentBrandId)
        .eq("status", "collected")
        .order("collection_date", { ascending: false });

      if (error) throw error;
      return data;
    },
    enabled: !!currentBrandId,
  });

  // Fetch programs
  const { data: programs } = useQuery({
    queryKey: ["newsroom-programs-for-bulletin", currentBrandId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("os_newsroom_programs")
        .select("*")
        .eq("brand_id", currentBrandId)
        .order("name", { ascending: true });

      if (error) throw error;
      return data;
    },
    enabled: !!currentBrandId,
  });

  // Fetch date formatting settings for selected program
  const { data: dateSettings } = useQuery({
    queryKey: ["newsroom-date-settings", selectedProgramId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("os_newsroom_settings")
        .select("date_formatting_enabled, date_formatting_locale, date_formatting_midnight_offset")
        .eq("program_id", selectedProgramId)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
    enabled: !!selectedProgramId,
  });

  // Compute preview date applying midnight offset when enabled
  const bulletinDatePreview = useMemo(() => {
    if (!selectedCollectionId) return null;
    const col = (collections || []).find((c: any) => c.id === selectedCollectionId);
    if (!col) return null;

    const base = new Date(col.collection_date);
    let applied = base;

    const enabled = dateSettings?.date_formatting_enabled;
    const offset = dateSettings?.date_formatting_midnight_offset ?? true;

    if (enabled && offset) {
      applied = applyMidnightOffset(base);
    }

    const moved = applied.toDateString() !== base.toDateString();
    const reason = moved ? "before midnight" : "after midnight";

    return { dateStr: applied.toLocaleDateString("en-US"), reason } as const;
  }, [selectedCollectionId, collections, dateSettings]);

  // Process collection to create bulletin
  const processBulletinMutation = useMutation({
    mutationFn: async () => {
      // Guard: ensure both collection and program are selected (not empty strings either)
      if (!selectedCollectionId || !selectedProgramId || selectedCollectionId.trim() === '' || selectedProgramId.trim() === '') {
        throw new Error("Please select both collection and program");
      }

      // Guard: ensure the selected collection actually has articles to process
      const selectedCollection = (collections || []).find((c: any) => c.id === selectedCollectionId);
      const articlesCount = selectedCollection?.total_articles ?? 0;
      if (!selectedCollection) {
        throw new Error("Selected collection not found");
      }
      if (articlesCount <= 0) {
        throw new Error("This collection has no articles. Please collect articles first or choose another collection.");
      }

      const { data, error } = await supabase.functions.invoke("newsroom-process-collection", {
        body: {
          collection_id: selectedCollectionId,
          program_id: selectedProgramId,
          brand_id: currentBrandId,
        },
      });

      if (error) throw error;
      return data;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["newsroom-bulletins"] });
      toast({
        title: "Bulletin Processing Started",
        description: "Script generation and video creation in progress...",
      });
      setIsProcessBulletinOpen(false);
      setSelectedCollectionId("");
      setSelectedProgramId("");
    },
    onError: (error: any) => {
      const msg = error?.message
        ? error.message
        : createEdgeFunctionErrorHandler("newsroom-process-collection")(error);
      toast({
        title: "Error processing collection",
        description: msg,
        variant: "destructive",
      });
    },
  });

  return (
    <Dialog open={isProcessBulletinOpen} onOpenChange={setIsProcessBulletinOpen}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitleWithBug
            popupName="Process Collection to Bulletin"
            popupContext="/newsroom/bulletins"
            isOpen={isProcessBulletinOpen}
          >
            Process Collection to Bulletin
          </DialogTitleWithBug>
          <DialogDescription>
            Select a collection and program to generate a bulletin
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label htmlFor="program">Program *</Label>
            <Select value={selectedProgramId} onValueChange={setSelectedProgramId}>
              <SelectTrigger id="program">
                <SelectValue placeholder="Select a program" />
              </SelectTrigger>
              <SelectContent>
                {programs?.map((program) => (
                  <SelectItem key={program.id} value={program.id}>
                    {program.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground mt-1">
              The program's prompts will be used to generate the bulletin content
            </p>
          </div>

          <div>
            <Label htmlFor="collection">Collection *</Label>
            <Select value={selectedCollectionId} onValueChange={setSelectedCollectionId}>
              <SelectTrigger id="collection">
                <SelectValue placeholder="Select a collection" />
              </SelectTrigger>
              <SelectContent>
                {collections?.map((collection) => (
                  <SelectItem key={collection.id} value={collection.id}>
                    {new Date(collection.collection_date).toLocaleDateString()} ({collection.total_articles} articles)
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground mt-1">
              Only collections with status "collected" are shown
            </p>
          </div>

          {selectedProgramId && (
            <Card className="bg-muted/50">
              <CardContent className="pt-4 text-sm space-y-2">
                <div className="font-medium">What will happen:</div>
                <ol className="list-decimal list-inside space-y-1 text-muted-foreground">
                  <li>Articles from collection will be processed</li>
                  <li>Program prompts will generate: script, headlines, summary</li>
                  <li>Uses Lovable AI with google/gemini-2.5-flash model</li>
                  <li>You can then manually create video with HeyGen</li>
                  {bulletinDatePreview && (
                    <li>
                      The date of the bulletin will be <span className="font-medium text-foreground">{bulletinDatePreview.dateStr}</span> as it's {bulletinDatePreview.reason}.
                    </li>
                  )}
                </ol>
              </CardContent>
            </Card>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setIsProcessBulletinOpen(false)}>
            Cancel
          </Button>
          <Button 
            onClick={() => processBulletinMutation.mutate()}
            disabled={!selectedCollectionId || !selectedProgramId || processBulletinMutation.isPending}
          >
            {processBulletinMutation.isPending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                <PlayCircle className="h-4 w-4 mr-2" />
                Generate Bulletin
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
